/**
 * @file index.tsx
 *
 * @fileoverview The branding for the app. Includes a logo linked to the homepage.
 */

import Link from '~/jobiqo/components/Link';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { AppContextInterface } from '~/jobiqo/models/app';
import React, { useContext } from 'react';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import BrandingSlot from '~/jobiqo/components/Slots/BrandingSlot';

/**
 * Check if a path matches a pattern with wildcards.
 *
 * @param path {string} - The current path to check.
 * @param pattern {string} - The pattern which may contain wildcards (*).
 * @returns {boolean} true if the path matches the pattern, false otherwise.
 */
const matchesPathPattern = (path: string, pattern: string): boolean => {
  // If the pattern is exactly '*', it matches everything.
  if (pattern === '*') {
    return true;
  }
  // Convert the pattern to a regex pattern
  // Replace * with regex wildcard that doesn't include slashes
  const regexPattern = pattern
    // Replace * with a capture group that doesn't match slashes.
    .replace(/\*/g, '([^/]*)')
    // Escape forward slashes for regex.
    .replace(/\//g, '\\/');
  // Create a regex that matches the entire path.
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(path);
};

/**
 * Checks if the current path should have the logo link disabled.
 *
 * @param {string} path - Current path.
 * @param {string[]} disabledRoutes - Array of routes/patterns where the link should be disabled.
 * @returns {boolean} - true if the link should be disabled, false otherwise.
 */
const shouldDisableLink = (path: string, disabledRoutes: string[]): boolean => {
  if (!path || !disabledRoutes || disabledRoutes.length === 0) {
    return false;
  }
  return disabledRoutes.some((route) => {
    // For exact matches.
    if (route === path) {
      return true;
    }
    // For patterns with wildcards.
    if (route.includes('*') && matchesPathPattern(path, route)) {
      return true;
    }
    return false;
  });
};

/**
 * Logo component for the branding.
 */
interface LogoProps {
  /**
   * The maximum height of the logo.
   */
  maxHeight?: string;
  /**
   * Custom logo to use for branding.
   */
  customLogo?: string;
  /**
   * The site name used for alt text.
   */
  sitename: string;
  /**
   * Default logo URL from config.
   */
  defaultLogo: string;
  /**
   * Logo minimum height from config.
   */
  minHeight: string;
}

const Logo: React.FC<LogoProps> = ({
  maxHeight,
  customLogo,
  sitename,
  defaultLogo,
  minHeight,
}) => {
  return (
    <span style={{ margin: 0 }}>
      <img
        {...(maxHeight
          ? { style: { maxHeight } }
          : {
              style: {
                minHeight: minHeight,
              },
            })}
        data-testid="branding-logo"
        src={customLogo ?? defaultLogo}
        alt={sitename}
      />
    </span>
  );
};

interface BrandingProps {
  /**
   * The maximum height of the logo.
   */
  maxHeight?: string;
  /**
   * Custom logo to use for branding.
   */
  customLogo?: string;
}

export const Branding = ({ maxHeight, customLogo }: BrandingProps) => {
  const appContext: AppContextInterface = useContext(AppContext);
  const { path } = useContext(PathContext);
  const { APP } = useFrontendConfig();
  const BRANDING_LOGO_LINK = APP.BRANDING_LOGO_LINK;
  const DISABLED_LOGO_LINK_ROUTES = APP.DISABLED_LOGO_LINK_ROUTES;
  const logoMinHeight = APP.THEME?.logo?.minHeight;
  const defaultLogo = APP.ASSETS?.logo;

  // Check if the current path is in the disabled routes list or matches a pattern.
  const isLinkDisabled = shouldDisableLink(path, DISABLED_LOGO_LINK_ROUTES);

  return (
    <div className="jd-branding">
      {isLinkDisabled ? (
        <div className="jd-branding-logo">
          <Logo
            maxHeight={maxHeight}
            customLogo={customLogo}
            sitename={appContext.sitename ?? ''}
            defaultLogo={defaultLogo}
            minHeight={logoMinHeight}
          />
        </div>
      ) : (
        <Link href={BRANDING_LOGO_LINK} className="jd-branding-logo">
          <Logo
            maxHeight={maxHeight}
            customLogo={customLogo}
            sitename={appContext.sitename ?? ''}
            defaultLogo={defaultLogo}
            minHeight={logoMinHeight}
          />
        </Link>
      )}
      <BrandingSlot />
    </div>
  );
};
